"use client"

import { useRef, useState, useEffect } from "react"
import { Input } from "../ui/input"
import { Button } from "../ui/button"
import { toast } from "sonner"
import { post } from "../../lib/api"
import { readFileWithEncodingDetection } from "../../lib/encoding-utils"

// InfoOverlay component for help
const InfoOverlay = ({ open, onClose }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 ">
            <div className="bg-[#ffffff] rounded-lg shadow-lg max-w-lg w-full p-6 relative dark:bg-[#2c313c] dark:text-white">
                <button
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl font-bold"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </button>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <svg width="22" height="22" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="256" cy="256" r="216" stroke="black" strokeWidth="32" fill="none"/>
                        <rect x="232" y="200" width="48" height="160" rx="24" fill="black"/>
                        <circle cx="256" cy="144" r="24" fill="black"/>
                    </svg>
                    How does the Spelling Bee planner work?
                </h2>
                <div className="space-y-3 text-gray-800 text-sm dark:text-white">
                    <p>
                        Upload a <b>.txt file</b> containing a single puzzle. The file must contain at least two lines.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><b>New Format (Recommended):</b> First line contains the puzzle in the format <b>Z	AEGLNS</b> (tab-separated: center letter + outer letters), followed by words on separate lines.</li>
                        <li><b>Old Format:</b> First line contains the puzzle in the format <b>A|M,O,P,R,S,V</b>, second line contains comma-separated words.</li>
                    </ul>
                    <div className="pt-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Example .txt file formats:</h3>
                        <div className="bg-gray-100 dark:bg-gray-900 p-3 rounded-md text-xs font-mono border dark:border-gray-700 space-y-2">
                            <div>
                                <p className="font-semibold text-blue-600">New Format:</p>
                                <p>Z	AEGLNS</p>
                                <p>ENGELENZANG</p>
                                <p>ALLEENZANG</p>
                                <p>AANZEGGEN</p>
                                <p>...</p>
                            </div>
                            <div>
                                <p className="font-semibold text-green-600">Old Format:</p>
                                <p>A|M,O,P,R,S,V</p>
                                <p>SPAARVORM,OPPASOMA,OPPASOPA,SPAARPAS,...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const SpellingBeePlanner = ({ game_id, onUpdate }) => {
    const [puzzles, setPuzzles] = useState([])
    const [currentPuzzleIndex, setCurrentPuzzleIndex] = useState(0)
    const [scheduledDate, setScheduledDate] = useState("")
    const [lastUploadedDate, setLastUploadedDate] = useState(null)
    const fileInputRef = useRef(null)
    const [showInfo, setShowInfo] = useState(false)

    useEffect(() => {
        const today = new Date()
        const todayString = today.toISOString().split("T")[0]
        setScheduledDate(todayString)
    }, [])

    useEffect(() => {
        if (lastUploadedDate) {
            const nextDay = new Date(lastUploadedDate)
            nextDay.setDate(nextDay.getDate() + 1)
            const nextDayString = nextDay.toISOString().split("T")[0]
            setScheduledDate(nextDayString)
        }
    }, [lastUploadedDate])

    // Parse a puzzle string like 'A|B,J,L,U,R,K' into {center: 'A', outers: ['B','J','L','U','R','K']}
    const parsePuzzleString = (str) => {
        const [center, outersRaw] = str.split("|")
        if (!center || !outersRaw) return null
        const outers = outersRaw.split(",").map(l => l.trim().toUpperCase()).filter(Boolean)
        if (center.length !== 1 || outers.length !== 6) return null
        const allLetters = [center.toUpperCase(), ...outers]
        // All must be unique and A-Z
        if (new Set(allLetters).size !== 7 || !allLetters.every(l => /^[A-Z]$/.test(l))) return null
        return { center: center.toUpperCase(), outers }
    }

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file || file.type !== "text/plain") {
            toast.error("Please upload a valid .txt file.")
            return
        }
        
        try {
            const text = await readFileWithEncodingDetection(file)
            const lines = text
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean)

            if (lines.length < 2) {
                toast.error("The file must contain at least two lines: one for letters and one or more for words.")
                return
            }

            // Parse the first line which contains the letters
            const firstLine = lines[0]
            let center, outers
            
            // Check if it's the new format (tab-separated) or old format (pipe-separated)
            if (firstLine.includes('\t')) {
                // New format: "Z	AEGLNS" (tab-separated)
                const [centerLetter, outerLetters] = firstLine.split('\t')
                if (!centerLetter || !outerLetters) {
                    toast.error("Invalid new format. First line should be: 'centerLetter\\touterLetters'")
                    return
                }
                center = centerLetter.trim()
                outers = outerLetters.trim().split('').map(l => l.trim().toUpperCase()).filter(Boolean)
                
                // Additional validation for new format
                if (center.length !== 1) {
                    toast.error("Center letter must be exactly one character.")
                    return
                }
                if (outers.length !== 6) {
                    toast.error("Outer letters must be exactly 6 characters.")
                    return
                }
                if (!/^[A-Z]$/.test(center)) {
                    toast.error("Center letter must be A-Z.")
                    return
                }
                if (!outers.every(l => /^[A-Z]$/.test(l))) {
                    toast.error("All outer letters must be A-Z.")
                    return
                }
            } else if (firstLine.includes('|')) {
                // Old format: "A|M,O,P,R,S,V" (pipe-separated)
                const parsed = parsePuzzleString(firstLine)
                if (!parsed) {
                    toast.error("The puzzle format on the first line is invalid.")
                    return
                }
                center = parsed.center
                outers = parsed.outers
            } else {
                toast.error("Invalid format. First line should be either 'centerLetter\\touterLetters' or 'centerLetter|outerLetter1,outerLetter2,...'")
                return
            }

            // Validate the letters
            if (center.length !== 1 || outers.length !== 6) {
                toast.error("Invalid letter count. Need 1 center letter and 6 outer letters.")
                return
            }
            
            const allLetters = [center.toUpperCase(), ...outers]
            if (new Set(allLetters).size !== 7 || !allLetters.every(l => /^[A-Z]$/.test(l))) {
                toast.error("Invalid letters. All letters must be unique A-Z characters.")
                return
            }

            // Parse words from remaining lines
            const words = lines.slice(1).map(line => line.trim()).filter(Boolean)
            
            if (words.length === 0) {
                toast.error("No valid words found in the file.")
                return
            }
            
            // Validate that words contain only letters and are reasonable length
            const invalidWords = words.filter(word => !/^[A-Z]+$/.test(word) || word.length < 3 || word.length > 20)
            if (invalidWords.length > 0) {
                toast.error(`Invalid words found: ${invalidWords.slice(0, 3).join(', ')}${invalidWords.length > 3 ? '...' : ''}. Words must be 3-20 letters long and contain only A-Z.`)
                return
            }

            const puzzle = {
                center: center.toUpperCase(),
                outers,
                validWords: words
            }

            setPuzzles([puzzle])
            setCurrentPuzzleIndex(0)
            toast.success(`Loaded 1 valid puzzle with ${words.length} words!`)
        } catch (error) {
            toast.error("Failed to read file. Please try again.")
            console.error("File reading error:", error)
        }
    }

    const getCurrentPuzzle = () => puzzles[currentPuzzleIndex] || null

    const formatDateForDisplay = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        })
    }

    const handleUploadPuzzle = async () => {
        const puzzle = getCurrentPuzzle()
        if (!puzzle) {
            toast.error("No valid puzzle to upload.")
            return
        }
        if (!scheduledDate) {
            toast.error("Please select a date for the challenge.")
            return
        }

        const puzzleString = `${puzzle.center}|${puzzle.outers.join(",")}`
        let lettersResponse = null
        let wordsResponse = null
        try {
            // API Call 1: Add challenge with letters
            lettersResponse = await post(
                {
                    method: "addChallengeNoDup",
                    game_id: game_id,
                    challenge_id: puzzleString,
                    data: puzzleString,
                    variant_id: 1,
                    variant_name: "letters",
                },
                "/challenges",
            )

        

            // API Call 2: Add challenge with words, if they exist
            if (puzzle.validWords && puzzle.validWords.length > 0) {
                wordsResponse = await post(
                    {
                        method: "addChallengeNoDup",
                        game_id: game_id,
                        challenge_id: puzzleString,
                        data: puzzle.validWords.join(","),
                        variant_id: 2,
                        variant_name: "words",
                    },
                    "/challenges",
                )
            }

            if (lettersResponse.error || wordsResponse.error) {
                toast.error(`Failed to upload challenge: A challenge with this name and data already exists`)
            }

            // API Call 3: Schedule the daily challenge
            const dailyResponse = await post(
                {
                    method: "addDailyChallenge",
                    game_id: game_id,
                    date: scheduledDate,
                    daily_challenge_info: puzzleString,
                },
                "/challenges",
            )

            if (dailyResponse.error) {
                toast.error(`Scheduling failed: ${dailyResponse.error}`)
                return
            }

            toast.success(`Challenge "${puzzleString}" scheduled for ${formatDateForDisplay(scheduledDate)}!`)
            setLastUploadedDate(scheduledDate)
            onUpdate?.()
        } catch (err) {
            toast.error("Upload failed!")
            console.error(err)
        }
    }

    // Honeycomb grid positions for 7 letters (center + 6 around)
    // We'll use a true hexagonal layout:
    //        0
    //     5     1
    //   4   C   2
    //      3
    // Map outers: [0,1,2,3,4,5], center: 6
    const getHoneycombGrid = () => {
        const puzzle = getCurrentPuzzle()
        if (!puzzle) return Array(7).fill("")
        const { center, outers } = puzzle
        const grid = Array(7).fill("")
        // Hex order: top, top-right, bottom-right, bottom, bottom-left, top-left, center
        grid[0] = outers[0] // top
        grid[1] = outers[1] // top-right
        grid[2] = outers[2] // bottom-right
        grid[3] = outers[3] // bottom
        grid[4] = outers[4] // bottom-left
        grid[5] = outers[5] // top-left
        grid[6] = center    // center
        return grid
    }

    const gridDisplay = getHoneycombGrid()

    return (
        <>
            <div className="space-y-4">
                {/* Only File Upload Entry */}
                <div className="flex items-center gap-3 mb-2">
                    <Input type="file" accept=".txt" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
                    <Button
                        className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white border border-blue-700 dark:border-blue-700 px-6 py-3 font-semibold shadow-md"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload Spelling Bee Puzzles File
                    </Button>
                </div>
            </div>

            {puzzles.length > 0 && (
                <>
    
                    {/* Honeycomb Grid Display */}
                    <div className="flex justify-center py-6 mb-2">
                        <div className="relative w-[280px] h-[260px]"> {/* Increased width/height for larger boxes */}
                            {/* Top */}
                            <div className="absolute left-1/2 -translate-x-1/2 top-[34px]">
                                <div className="w-14 h-14 flex items-center justify-center text-2xl font-bold bg-gray-200 dark:bg-gray-700 rounded-xl border border-gray-300 dark:border-gray-600">{gridDisplay[0]}</div>
                            </div>
                            {/* Top-right */}
                            <div className="absolute left-[calc(100%*4.4/6-28px)] top-[68px]"> {/* More right, adjusted for larger box */}
                                <div className="w-14 h-14 flex items-center justify-center text-2xl font-bold bg-gray-200 dark:bg-gray-700 rounded-xl border border-gray-300 dark:border-gray-600">{gridDisplay[1]}</div>
                            </div>
                            {/* Bottom-right */}
                            <div className="absolute left-[calc(100%*4.4/6-28px)] bottom-[68px]"> {/* More right, adjusted for larger box */}
                                <div className="w-14 h-14 flex items-center justify-center text-2xl font-bold bg-gray-200 dark:bg-gray-700 rounded-xl border border-gray-300 dark:border-gray-600">{gridDisplay[2]}</div>
                            </div>
                            {/* Bottom */}
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-[34px]">
                                <div className="w-14 h-14 flex items-center justify-center text-2xl font-bold bg-gray-200 dark:bg-gray-700 rounded-xl border border-gray-300 dark:border-gray-600">{gridDisplay[3]}</div>
                            </div>
                            {/* Bottom-left */}
                            <div className="absolute left-[calc(100%*1.6/6-28px)] bottom-[68px]"> {/* More left, adjusted for larger box */}
                                <div className="w-14 h-14 flex items-center justify-center text-2xl font-bold bg-gray-200 dark:bg-gray-700 rounded-xl border border-gray-300 dark:border-gray-600">{gridDisplay[4]}</div>
                            </div>
                            {/* Top-left */}
                            <div className="absolute left-[calc(100%*1.6/6-28px)] top-[68px]"> {/* More left, adjusted for larger box */}
                                <div className="w-14 h-14 flex items-center justify-center text-2xl font-bold bg-gray-200 dark:bg-gray-700 rounded-xl border border-gray-300 dark:border-gray-600">{gridDisplay[5]}</div>
                            </div>
                            {/* Center */}
                            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                <div className="w-14 h-14 flex items-center justify-center text-2xl font-bold bg-blue-400 dark:bg-blue-700 text-white rounded-xl">{gridDisplay[6]}</div>
                            </div>
                        </div>
                    </div>

                    {/* Puzzle Info */}
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-900 rounded-lg mb-2">
                        <div className="text-base text-gray-900 dark:text-white space-y-1">
                            <div>
                                <strong>Puzzle Format:</strong> {getCurrentPuzzle() && `${getCurrentPuzzle().center}|${getCurrentPuzzle().outers.join(",")}`}
                            </div>
                            {/* Display valid words if available */}
                            {getCurrentPuzzle() && getCurrentPuzzle().validWords && getCurrentPuzzle().validWords.length > 0 && (
                                <div className="mt-2">
                                    <strong>Valid Words:</strong>
                                    <div className="max-h-32 overflow-y-auto bg-[#ffffff] dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 mt-1 text-sm">
                                        {getCurrentPuzzle().validWords.join(", ")}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Date Scheduling */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-900 rounded-lg mb-2">
                        <div className="space-y-3">
                            <label htmlFor="scheduledDate" className="block text-sm font-medium text-gray-900 dark:text-white">
                                Schedule Challenge Date:
                            </label>
                            <div className="flex items-center gap-4">
                                <Input
                                    type="date"
                                    id="scheduledDate"
                                    value={scheduledDate}
                                    onChange={(e) => setScheduledDate(e.target.value)}
                                    className="max-w-xs text-gray-900 dark:text-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600"
                                />
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                    {scheduledDate && `Will be scheduled for: ${formatDateForDisplay(scheduledDate)}`}
                                </div>
                            </div>
                            {lastUploadedDate && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Last upload was scheduled for {formatDateForDisplay(lastUploadedDate)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Upload Button */}
                    <Button
                        className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white border border-green-700 dark:border-green-700 py-3 font-semibold shadow-md mb-2"
                        onClick={handleUploadPuzzle}
                    >
                        Schedule This Puzzle for {scheduledDate ? formatDateForDisplay(scheduledDate) : "Selected Date"}
                    </Button>
                </>
            )}

            {puzzles.length === 0 && (
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-md mb-4 mt-4">
                    <span>
                        �� Upload a .txt file containing a single puzzle (supports both new and old formats)
                    </span>
                    <button
                        className="ml-2 p-1 rounded-full flex items-center justify-center"
                        onClick={() => setShowInfo(true)}
                        aria-label="How does the planner work?"
                        type="button"
                    >
                        <svg width="20" height="20" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="256" cy="256" r="216" stroke="currentColor" strokeWidth="40" fill="none"/>
                            <rect x="232" y="200" width="48" height="160" rx="24" fill="currentColor"/>
                            <circle cx="256" cy="144" r="24" fill="currentColor"/>
                        </svg>
                    </button>
                </div>
            )}
            <InfoOverlay open={showInfo} onClose={() => setShowInfo(false)} />
        </>
    )
}

export default SpellingBeePlanner 